import { useState, useEffect } from "react";
import { toast } from "@/lib/toast";
import { Mail, ArrowRight, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import api from "@/lib/api";

/**
 * 2-step admin e-mail change with OTP confirmation sent to the NEW address.
 *  Step 1 — collect new e-mail → POST ${endpoint}/request
 *  Step 2 — collect the 6-digit code → POST ${endpoint}/verify
 *
 * `endpoint` defaults to /users/me/email-change (self-edit from Mon profil).
 * Pass /users/{id}/email-change to edit another user from the admin modal.
 */
export default function EmailChangeDialog({
  open, onOpenChange, currentEmail, onChanged,
  endpoint = "/users/me/email-change",
  targetName,
}) {
  const [step, setStep] = useState(1);
  const [newEmail, setNewEmail] = useState("");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setNewEmail("");
      setCode("");
    }
  }, [open]);

  const sendCode = async (e) => {
    e?.preventDefault();
    setSending(true);
    try {
      await api.post(`${endpoint}/request`, { new_email: newEmail.trim().toLowerCase() });
      toast.success("Code envoyé", {
        description: `6 chiffres, adressé à ${newEmail.trim().toLowerCase()}`,
      });
      setStep(2);
    } catch (err) {
      toast.error("Envoi refusé", { description: err.response?.data?.detail || "Réessayez." });
    } finally { setSending(false); }
  };

  const verify = async (e) => {
    e?.preventDefault();
    setVerifying(true);
    try {
      const { data } = await api.post(`${endpoint}/verify`, { code: code.trim() });
      toast.success("E-mail mis à jour", {
        description: "Utilisez votre nouvelle adresse à la prochaine connexion",
      });
      onChanged?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error("Code refusé", { description: err.response?.data?.detail || "Réessayez." });
    } finally { setVerifying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-vertec-borderDark max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail size={18} className="text-vertec-blue" />
            {targetName ? `Modifier l'e-mail de ${targetName}` : "Modifier mon adresse e-mail"}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Pour valider le changement, un code de sécurité sera envoyé à la nouvelle adresse."
              : "Consultez la nouvelle boîte de réception et saisissez le code à 6 chiffres."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <form onSubmit={sendCode} className="space-y-4">
            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                Adresse actuelle
              </Label>
              <div className="text-sm text-muted-foreground">{currentEmail}</div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-email">Nouvelle adresse</Label>
              <Input
                id="new-email"
                type="email"
                required
                placeholder="ex : nouveau@vertec.dz"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value.toLowerCase())}
                data-testid="email-change-new"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline"
                className="border-vertec-borderDark"
                onClick={() => onOpenChange(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={sending || !newEmail.trim()}
                className="bg-vertec-blue hover:bg-vertec-blueHover text-white gap-2"
                data-testid="email-change-send">
                <ArrowRight size={14} />
                {sending ? "Envoi…" : "Envoyer le code"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <form onSubmit={verify} className="space-y-4">
            <div className="rounded-md border border-vertec-borderDark bg-background/40 p-3 text-xs text-muted-foreground">
              Code envoyé à <strong className="text-foreground">{newEmail}</strong>. Valable 15 minutes.
            </div>
            <div className="space-y-2">
              <Label htmlFor="ec-code">Code à 6 chiffres</Label>
              <Input
                id="ec-code"
                required
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={6}
                placeholder="123 456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="text-lg tracking-[0.3em] text-center font-mono"
                data-testid="email-change-code"
              />
            </div>
            <DialogFooter className="!justify-between">
              <Button type="button" variant="ghost" size="sm"
                onClick={() => setStep(1)}>
                Modifier l&apos;adresse
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline"
                  className="border-vertec-borderDark"
                  onClick={() => onOpenChange(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={verifying || code.length !== 6}
                  className="bg-vertec-blue hover:bg-vertec-blueHover text-white gap-2"
                  data-testid="email-change-verify">
                  <ShieldCheck size={14} />
                  {verifying ? "Vérification…" : "Confirmer"}
                </Button>
              </div>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
